'use client';

import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useUser } from '@/hooks/auth';
import { useTenant } from '@/hooks/queries/tenant';

import { useTour } from '@/components/tour';

import { SIDEBAR_TOUR_STEPS } from '@/lib/tour-config';

import { QueryKeys } from '@/constants/query-keys';

import { RotateScreenPrompt } from './RotateScreenPrompt';
import { TourWelcomePrompt } from './TourWelcomePrompt';

function useIsTouchDevice(): boolean {
  const [isTouchDevice] = useState(() => {
    if (typeof window === 'undefined') return false;
    return navigator.maxTouchPoints > 0 || window.matchMedia('(pointer: coarse)').matches;
  });
  return isTouchDevice;
}

function useIsLandscape(): boolean | null {
  const [isLandscape, setIsLandscape] = useState<boolean | null>(null);

  useEffect(() => {
    const check = () => {
      if (typeof window !== 'undefined') {
        setIsLandscape(window.innerWidth > window.innerHeight);
      }
    };

    check();
    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', check);
    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('orientationchange', check);
    };
  }, []);

  return isLandscape;
}

export function TourManager() {
  const { data: user } = useUser();
  const { data: tenant, isLoading } = useTenant();
  const {
    isActive,
    currentStep,
    totalSteps,
    start,
    next,
    prev,
    complete,
    skip,
    registerOnEnd,
  } = useTour();
  const router = useRouter();
  const qc = useQueryClient();
  const hasTouchDevice = useIsTouchDevice();
  const isLandscape = useIsLandscape();
  const hasInitialized = useRef(false);
  const [imgError, setImgError] = useState(false);
  const [welcomeDismissed, setWelcomeDismissed] = useState(false);

  // Show rotate prompt when touch device in portrait with pending tour
  const showRotatePrompt =
    hasTouchDevice &&
    isLandscape === false &&
    !!user &&
    !isLoading &&
    tenant?.tour_taken === false;

  // Show welcome card once landscape is detected on touch device (before tour starts)
  const showWelcomePrompt =
    hasTouchDevice &&
    isLandscape === true &&
    !!user &&
    !isLoading &&
    tenant?.tour_taken === false &&
    !isActive &&
    !welcomeDismissed;

  const markTourDone = useCallback(async () => {
    // Exit fullscreen if we entered it (no-op on iOS Safari where fullscreenElement is always null)
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
    } catch {
      /* not supported */
    }
    try {
      await fetch('/api/tenant/profile', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tour_taken: true }),
      });
      qc.setQueryData(
        [QueryKeys.TENANT],
        (prev: Record<string, unknown> | undefined) =>
          prev ? { ...prev, tour_taken: true } : prev,
      );
    } catch {
      // Best-effort
    }
    router.push('/pos/calendar');
  }, [qc, router]);

  useEffect(() => {
    registerOnEnd(markTourDone);
  }, [registerOnEnd, markTourDone]);

  // Auto-start tour on desktop only — touch devices use the welcome prompt instead
  useEffect(() => {
    if (isLoading || !tenant || !user || hasInitialized.current) return;
    if (tenant.tour_taken !== false) return;
    if (hasTouchDevice) return;

    hasInitialized.current = true;
    const timer = setTimeout(() => start(SIDEBAR_TOUR_STEPS), 600);
    return () => clearTimeout(timer);
  }, [tenant, isLoading, user, hasTouchDevice, start]);

  // Navigate to the step's page when it has a path
  useEffect(() => {
    if (!isActive) return;
    const step = SIDEBAR_TOUR_STEPS[currentStep];
    if (step?.path) router.push(step.path);
  }, [currentStep, isActive, router]);

  const step = isActive ? SIDEBAR_TOUR_STEPS[currentStep] : null;

  // Reset image error state when step changes
  useEffect(() => {
    setImgError(false);
  }, [currentStep]);

  // "Start Tour" handler — fullscreen must be requested on a user gesture to work
  const handleStartTour = async () => {
    setWelcomeDismissed(true);

    if (typeof window !== 'undefined' && document.documentElement.requestFullscreen) {
      try {
        await document.documentElement.requestFullscreen();
        if ('orientation' in screen && (screen.orientation as any).lock) {
          await (screen.orientation as any).lock('landscape').catch(() => {});
        }
      } catch {
        console.warn('Fullscreen request ignored or unsupported.');
      }
    }

    start(SIDEBAR_TOUR_STEPS);
  };

  // "Skip" on welcome card — never entered fullscreen, just mark done
  const handleSkipFromWelcome = () => {
    setWelcomeDismissed(true);
    markTourDone();
  };

  if (showRotatePrompt) {
    return <RotateScreenPrompt />;
  }

  if (showWelcomePrompt) {
    return <TourWelcomePrompt onStart={handleStartTour} onSkip={handleSkipFromWelcome} />;
  }

  if (!isActive || !step) return null;

  const isLeft = step.position === 'left';

  return (
    <AnimatePresence>
      {/* Blocking backdrop — prevents any interaction with the page behind */}
      <motion.div
        key='tour-backdrop'
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className='pointer-events-auto fixed inset-0 z-[150] bg-black/50'
        onClick={skip}
      />

      {hasTouchDevice ? (
        /* ── Mobile: centered card, whole card scrolls ────────────────── */
        <div className='pointer-events-none fixed inset-0 z-[200] flex items-center justify-center'>
          <motion.div
            key={`tour-step-mobile-${currentStep}`}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.25 }}
            className='pointer-events-auto mx-4 flex w-full max-w-sm flex-col overflow-y-auto rounded-xl border bg-background shadow-2xl'
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top controls: skip | prev/next — always visible */}
            <div className='flex items-center justify-between border-b px-3 py-2.5'>
              <button
                onClick={skip}
                className='text-xs text-muted-foreground/60 transition-colors hover:text-muted-foreground'
              >
                Skip
              </button>
              <span className='text-xs text-muted-foreground'>
                {currentStep + 1} / {totalSteps}
              </span>
              <div className='flex items-center gap-1.5'>
                {currentStep > 0 && (
                  <button
                    onClick={prev}
                    className='rounded-md px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted'
                  >
                    Back
                  </button>
                )}
                {currentStep < totalSteps - 1 ? (
                  <button
                    onClick={next}
                    className='rounded-md bg-blue-500 px-3 py-1 text-xs font-medium text-white transition-opacity hover:opacity-80'
                  >
                    Next
                  </button>
                ) : (
                  <button
                    onClick={complete}
                    className='rounded-md bg-blue-500 px-3 py-1 text-xs font-medium text-white transition-opacity hover:opacity-80'
                  >
                    Done
                  </button>
                )}
              </div>
            </div>

            {/* Step progress bar */}
            <div className='flex gap-1 px-3 pt-2'>
              {SIDEBAR_TOUR_STEPS.map((_, i) => (
                <div
                  key={i}
                  className={`h-1 flex-1 rounded-full transition-colors ${
                    i <= currentStep ? 'bg-blue-500' : 'bg-blue-100/40'
                  }`}
                />
              ))}
            </div>

            {/* GIF — centered, constrained width */}
            {step.gifUrl && !imgError && (
              <div className='flex justify-center px-3 pt-2'>
                <div className='w-full max-w-[280px] overflow-hidden rounded-lg border'>
                  <img
                    src={step.gifUrl}
                    alt={step.title}
                    className='aspect-video w-full object-cover'
                    onError={() => setImgError(true)}
                  />
                </div>
              </div>
            )}

            {/* Fallback when image fails */}
            {step.gifUrl && imgError && (
              <div className='mx-3 mt-1 flex aspect-video items-center justify-center rounded-lg border bg-muted'>
                <span className='text-xs text-muted-foreground'>
                  Preview unavailable
                </span>
              </div>
            )}

            {/* Content */}
            <div className='px-3 pb-3 pt-2'>
              <h3 className='text-sm font-semibold'>{step.title}</h3>
              <p className='mt-1.5 text-xs leading-relaxed text-muted-foreground'>
                {step.description}
              </p>
            </div>
          </motion.div>
        </div>
      ) : (
        /* ── Desktop: existing card ─────────────────────────────────── */
        <motion.div
          key={`tour-step-${currentStep}`}
          initial={{ opacity: 0, scale: 0.95, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 8 }}
          transition={{ duration: 0.2 }}
          style={{
            width: step.customWidth || '720px',
          }}
          className={`pointer-events-auto fixed z-[200] flex max-h-[calc(100vh-3rem)] flex-col overflow-hidden rounded-xl border bg-background shadow-2xl ${
            isLeft
              ? `${step.customLeftOffset || 'left-6'} top-6`
              : 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className='flex items-center justify-between border-b px-5 py-3.5'>
            <h2 className='text-sm font-semibold'>Quick Tour</h2>
            <span className='text-xs text-muted-foreground'>
              {currentStep + 1} of {totalSteps}
            </span>
          </div>

          {/* Step progress bar */}
          <div className='flex gap-1.5 px-5 py-2.5'>
            {SIDEBAR_TOUR_STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 flex-1 rounded-full transition-colors ${
                  i <= currentStep ? 'bg-blue-500' : 'bg-blue-100/40'
                }`}
              />
            ))}
          </div>

          {/* GIF — full width, 16:9 */}
          {step.gifUrl && !imgError && (
            <div className='mx-5 mt-1 overflow-hidden rounded-lg border'>
              <img
                src={step.gifUrl}
                alt={step.title}
                className='aspect-video w-full object-cover'
                onError={() => setImgError(true)}
              />
            </div>
          )}

          {/* Fallback when image fails */}
          {step.gifUrl && imgError && (
            <div className='mx-5 mt-1 flex aspect-video items-center justify-center rounded-lg border bg-muted'>
              <span className='text-xs text-muted-foreground'>
                Preview unavailable
              </span>
            </div>
          )}

          {/* Content */}
          <div className='min-h-0 flex-1 overflow-y-auto px-5 pb-4 pt-3'>
            <h3 className='text-base font-semibold'>{step.title}</h3>
            <p className='mt-1.5 text-sm leading-relaxed text-muted-foreground'>
              {step.description}
            </p>
          </div>

          {/* Footer */}
          <div className='flex items-center justify-between border-t px-5 py-3'>
            <button
              onClick={skip}
              className='text-xs text-muted-foreground/60 transition-colors hover:text-muted-foreground'
            >
              Skip tour
            </button>
            <div className='flex items-center gap-2'>
              {currentStep > 0 && (
                <button
                  onClick={prev}
                  className='rounded-md px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted'
                >
                  Previous
                </button>
              )}
              {currentStep < totalSteps - 1 ? (
                <button
                  onClick={next}
                  className='rounded-md bg-blue-500 px-4 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-80'
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={complete}
                  className='rounded-md bg-blue-500 px-4 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-80'
                >
                  Finish
                </button>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
